import type { ContentObjectView, KudoContentObjectDetail, KudoContentObjectList } from 'models';
import { type ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canDeleteContentObjectProfile, canEditContentObjectProfile, usePermissions } from 'permissions';
import { BookmarkLineIcon, Delete2LineIcon, Flag1LineIcon, type MenuProps, PencilLineIcon, message } from 'ui';
import { useUser } from 'features/auth';
import { useUnbookmarkObjectMutation } from 'features/bookmark';
import { useReportContentObjectModalConfirm } from 'features/report';
import { EditKudoModal } from '../../../edit-kudo-modal';
import { useDeleteKudoConfirmModal } from './useDeleteKudoConfirmModal';

type P = {
  item: KudoContentObjectDetail | KudoContentObjectList;
  view: ContentObjectView;
};

type UseCreateActionsReturn = {
  actions: MenuProps['items'];
  contexts?: ReactNode[];
  editModal?: ReactNode;
};

export const useCreateActions = ({ item, view }: P): UseCreateActionsReturn => {
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);
  const { t } = useTranslation();
  const { confirm: confirmDeleteKudo, contextHolder: deleteContentObjectContextHolder } = useDeleteKudoConfirmModal();

  const { confirm: confirmReportKudo, contextHolder: reportContentObjectContextHolder } =
    useReportContentObjectModalConfirm();

  const { canReportContentObject } = usePermissions();
  const { railsUserData: viewer } = useUser();
  const { mutate: unbookmark } = useUnbookmarkObjectMutation();

  return useMemo(() => {
    if (view === 'bookmark') {
      return {
        actions: [
          {
            icon: <BookmarkLineIcon size="small" />,
            key: 'unbookmark',
            label: t('Unbookmark'),
            onClick() {
              unbookmark(
                { contentObject: item, type: 'rails' },
                {
                  onError() {
                    message.error("Something went wrong. Couldn't be unbookmarked right now.");
                  },
                  onSuccess() {
                    message.success('Successfully unbookmarked');
                  },
                },
              );
            },
          },
        ],
      };
    }

    const actions = [];

    if (canEditContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        icon: <PencilLineIcon size="small" />,
        key: 'update',
        label: t('Edit'),
        onClick() {
          setIsEditModalOpened(true);
        },
      });
    }

    if (canReportContentObject({ contentObject: item })) {
      actions.push({
        icon: <Flag1LineIcon size="small" />,
        key: 'report',
        label: t('Report'),
        onClick() {
          confirmReportKudo({
            id: item.id,
            onSuccess() {
              message.success('Successfully reported');
            },
            title: 'Are you sure you want to report this kudo?',
          });
        },
      });
    }

    if (canDeleteContentObjectProfile({ contentObject: item, viewer })) {
      actions.push({
        danger: true,
        icon: <Delete2LineIcon color="error" size="small" />,
        key: 'delete',
        label: t('Delete'),
        onClick() {
          confirmDeleteKudo({
            item,
            onSuccess() {
              message.success('Successfully deleted');
            },
            title: 'Are you sure you want to delete this kudo?',
          });
        },
      });
    }

    const editModal = (
      <EditKudoModal
        isOpened={isEditModalOpened}
        kudoContentObject={item}
        onCancel={() => {
          setIsEditModalOpened(false);
        }}
      />
    );

    return {
      actions,
      contexts: [deleteContentObjectContextHolder, reportContentObjectContextHolder],
      editModal,
    };
  }, [
    view,
    canReportContentObject,
    item,
    viewer,
    isEditModalOpened,
    deleteContentObjectContextHolder,
    reportContentObjectContextHolder,
    t,
    confirmReportKudo,
    confirmDeleteKudo,
    unbookmark,
  ]);
};
